import styled, { css } from 'styled-components';

import { PinnedBar, PINNED_BAR_PILL_REACH_PX } from '../../../../components/PinnedBar';
import { Text } from '../../../../components/Text/Text';
import { Count as ChipCount, BADGE_COLORS } from '../../../../components/Tab/Tab.styles';
import {
  AFFILIATE_FORM,
  ANIM,
  COLORS,
  FIXED_HEADER,
  HEADER_DESKTOP_PX,
  HEADER_MOBILE_PX,
  MOBILE,
  REDUCED_MOTION,
  SPACE,
} from '@/nova/theme/tokens';
import { scrollFadeMaskY } from '@/nova/theme/mixins';
import {
  ACTIONS_ROW_PIN_TOP,
  DIALOG_BAND_PX,
  DIALOG_FILTER_CLEARANCE,
  FILTER_BAR_PX,
  FILTER_CLEARANCE,
  FILTER_PIN_TOP,
  HELD_HEAD_VAR,
  type ListPlacement,
} from '../../game-list-geometry';

/** The pinned area header above the list on a Website area page with the hub on (the Website shell publishes its
 *  pinned box); nothing anywhere else. Every on-page pin and match landing below adds it. */
const BACK_HEADER = 'var(--ows-back-header-offset, 0px)';

/** The narrowest the field gets beside its actions before they give up their labels. */
export const FIELD_MIN_PX = 240;

/* The field and the actions on one row, the actions keeping to the right and wrapping under the field only when even
   their icons do not fit. Off the scale on purpose: the 10px gap is ROW_GAP_PX's (GameListFilter), which the compact
   point is measured against, and the skeleton's filter row is this same row. */
const rowLayout = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

/** The row's layout on its own, the field and the actions with no frame: what the loading skeleton stands in with. */
export const RowLayout = styled.div`
  ${rowLayout}
`;

/** The fill bridging the band above the row held in a dialog: the game-list sheet's own surface. */
export const DIALOG_BRIDGE = AFFILIATE_FORM.surface;

/*
 * The row carrying the list's actions (the field and the actions): ALWAYS pinned, not only while filtering, a
 * PinnedBar. At rest it is the field and the actions, no frame, as the Stats header is its title; held at its pin it
 * lifts into the Stats header's pill (a pill's reach, 10px, above and below the bar, 8px past either side), over a fill
 * bridging up to what is pinned above it, so nothing scrolling under it shows. Above the page header's scrim
 * (z-index 1), which otherwise tints the row's buttons and field as it fades over them.
 *
 * On the page it pins where `game-list-geometry` says (ACTIONS_ROW_PIN_TOP), below the pinned area header on a Website
 * area page with the hub on. On a phone, while it shows the open list's name over the bar (`$heading`), the pill reaches
 * up over the name. In a dialog the row is a band, 6px either side of its bar, and pins near the top of the scroll
 * area, the room of its pill above it. Either way it ends where a match scrolled into view lands
 * (DIALOG_FILTER_CLEARANCE / FILTER_CLEARANCE).
 */
export const PinnedRow = styled(PinnedBar)<{ $placement: ListPlacement }>`
  ${rowLayout}
  /* On the page, under the pinned area header (BackHeader, z-index 5) and over the page's scrim (1): its fill reaches up
     to the top bar, behind the header, and drawn over it, it took the header away as the games scrolled (the user,
     2026-10-21, once the tab no longer stood in a stacking context of its own). */
  z-index: ${({ $placement }) => ($placement === 'page' ? 4 : 6)};

  ${({ $placement }) =>
    $placement === 'dialog'
      ? css`
          padding: ${DIALOG_BAND_PX}px 0;
          --pinned-bar-pill-top: ${DIALOG_BAND_PX - PINNED_BAR_PILL_REACH_PX}px;
          --pinned-bar-pill-bottom: ${DIALOG_BAND_PX - PINNED_BAR_PILL_REACH_PX}px;
        `
      : css`
          --pinned-bar-offset: calc(${ACTIONS_ROW_PIN_TOP.desktop - HEADER_DESKTOP_PX}px + ${BACK_HEADER});

          ${FIXED_HEADER} {
            --pinned-bar-offset: calc(${ACTIONS_ROW_PIN_TOP.phone - HEADER_MOBILE_PX}px + ${BACK_HEADER});

            /* Held, it is no pill on a phone: across the screen, over a see-through layer of the page that blurs the games
               passing under it and fades out past it, as the columns' held heads do (the user, 2026-10-20). */
            &&::after {
              inset: var(--pinned-bar-pill-top, -${PINNED_BAR_PILL_REACH_PX}px) -${SPACE[16]} -${SPACE[16]};
              border: 0;
              border-radius: 0;
              box-shadow: none;
              background: color-mix(in srgb, ${COLORS.pageBg} 80%, transparent);
              backdrop-filter: blur(12px);
              -webkit-backdrop-filter: blur(12px);
              ${scrollFadeMaskY(false, true, 16)}
            }
          }
        `}
`;

/*
 * The field's place above the list, without actions on its row. Once a filter is typed it FLOATS: sticky, so it
 * pins at the top of the screen for as long as the list scrolls under it. Sticky rather than fixed on purpose: the
 * Creatives tab switch animation leaves a transform on an ancestor, which would anchor a fixed bar to that ancestor
 * instead of the screen, and the same input element stays mounted (no focus hand-off, the phone keyboard stays up).
 *
 * The pin clears the layout chrome the way BackHeader does (`game-list-geometry`). On desktop it also clears
 * PageShell's header scrim band: the list lives inside a stacking context the scrim paints over, so a bar
 * pinned inside that band would be faded by it.
 */
export const Slot = styled.div<{ $floating: boolean; $placement: ListPlacement }>`
  ${({ $floating, $placement }) =>
    $floating &&
    $placement === 'dialog' &&
    css`
      /* In a dialog: flush to the top of its scroll area, over the games scrolling under it. */
      position: sticky;
      top: 0;
      z-index: 5;
    `}

  ${({ $floating, $placement }) =>
    $floating &&
    $placement === 'page' &&
    css`
      position: sticky;
      top: calc(${FILTER_PIN_TOP.desktop}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + ${BACK_HEADER});
      z-index: 5;

      ${FIXED_HEADER} {
        top: calc(
          ${FILTER_PIN_TOP.phone}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + var(--ows-imp-offset, 0px) +
            ${BACK_HEADER}
        );
      }
    `}
`;

/*
 * Where a match scrolls to: just below the pinned filter, so it stays visible above a phone keyboard. Spread
 * on each game's element (the target of `scrollIntoView`).
 */
export const matchScrollMargin = (placement: ListPlacement) =>
  placement === 'dialog'
    ? css`
        scroll-margin-top: var(${HELD_HEAD_VAR}, ${DIALOG_FILTER_CLEARANCE}px);
      `
    : css`
        scroll-margin-top: calc(
          ${FILTER_CLEARANCE.desktop}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + ${BACK_HEADER}
        );

        ${FIXED_HEADER} {
          scroll-margin-top: calc(
            ${FILTER_CLEARANCE.phone}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + var(--ows-imp-offset, 0px) +
              ${BACK_HEADER}
          );
        }
      `;

/** How long the folded field takes to open across the row, the actions stepping out of its way (and back). */
const FOLD_MS = 240;

/* The field itself; floating, it becomes a raised panel that carries the match count and the steps. At rest it is the
   column of lists' search (PageTree's SearchBox and SearchInput): its height, corners, frame and focus, its magnifier
   12px in and its text 36px in, as that one's are. */
export const Bar = styled.div<{ $floating: boolean; $fold?: 'folded' | 'open' }>`
  flex: 1 1 ${FIELD_MIN_PX}px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${SPACE[4]};
  box-sizing: border-box;
  max-width: 560px;
  height: ${FILTER_BAR_PX}px;
  padding: 0 4px 0 11px;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.textMuted};
  transition:
    background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    box-shadow ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:focus-within {
    border-color: ${COLORS.accent};
  }

  /* The magnifier keeps its size however narrow the field is (folded, it is all the field shows). */
  & > svg {
    flex: none;
  }

  ${({ $floating }) =>
    $floating &&
    css`
      background: ${COLORS.surfaceCard};
      box-shadow: ${COLORS.menuShadow};
    `}

  ${MOBILE} {
    max-width: none;
  }

  /* On a phone, folded away (GameListFilterProps.collapsible): its magnifier alone in its frame, at the row's left; open,
     it grows across the row from there. Its grow, not its width, moves, so nothing else on the row jumps. */
  ${({ $fold }) =>
    $fold &&
    css`
      position: relative;
      flex: ${$fold === 'folded' ? 0 : 1} 1 ${FILTER_BAR_PX}px;
      min-width: ${FILTER_BAR_PX}px;
      max-width: none;
      overflow: hidden;
      transition:
        flex-grow ${FOLD_MS}ms ${ANIM.EASE},
        background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
        box-shadow ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
        border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

      ${REDUCED_MOTION} {
        transition: none;
      }
    `}
`;

/* Folded, the whole frame is the button that opens the field (the magnifier under it shows through). */
export const FoldBtn = styled.button`
  position: absolute;
  inset: 0;
  padding: 0;
  border: 0;
  border-radius: inherit;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

/* The row's actions beside a folding field: they step out of its way as it opens (fading, narrowing to nothing) and
   come back as it folds (their room first, unseen, then fading in). On one line throughout, so the row keeps its height
   (wrapped into a column as they narrowed, they pushed the list down); cut off while away, never while shown (their
   kebab's menu opens out of them). */
export const ActionsFold = styled.div<{ $away: boolean }>`
  display: flex;
  justify-content: flex-end;
  min-width: 0;
  margin-left: auto;
  max-width: ${({ $away }) => ($away ? '0' : '100%')};
  overflow-x: ${({ $away }) => ($away ? 'clip' : 'visible')};
  opacity: ${({ $away }) => ($away ? 0 : 1)};
  visibility: ${({ $away }) => ($away ? 'hidden' : 'visible')};
  transition:
    max-width ${FOLD_MS}ms ${ANIM.EASE},
    opacity ${FOLD_MS / 2}ms ${ANIM.EASE} ${({ $away }) => ($away ? 0 : FOLD_MS)}ms,
    visibility 0s linear ${({ $away }) => ($away ? FOLD_MS : 0)}ms;

  /* Over the actions' own wrap (Actions, defined later at the same weight). */
  && > * {
    flex-wrap: nowrap;
    max-width: none;
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;


export const Input = styled.input<{ $folded?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  /* Folded away, out of sight until its field has opened. */
  opacity: ${({ $folded }) => ($folded ? 0 : 1)};
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  height: 100%;
  margin-left: 4px;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${COLORS.text};
  font-family: inherit;
  /* 16px keeps iOS from zooming the page when the field takes focus. */
  font-size: 16px;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

export const Count = styled.span`
  flex: none;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${COLORS.textMuted};
`;

export const IconBtn = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${COLORS.heading};
  cursor: pointer;
  transition:
    background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled),
  &:focus-visible {
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const ClearBtn = styled(IconBtn)`
  width: 32px;
  height: 32px;
  color: ${COLORS.textMuted};
`;

export const Divider = styled.span`
  flex: none;
  width: 1px;
  height: 22px;
  margin: 0 ${SPACE[2]};
  background: ${COLORS.hairline};
`;

/* The actions on the field's row: at the right end, keeping their size. */
export const Actions = styled.div`
  flex: none;
  display: inline-flex;
  /* The group sizes to its content and never shrinks, so without a ceiling it would run past the row
     rather than wrap: an action that keeps its label when compact (Publish does) can make it wider than
     a phone. The ceiling is what gives flex-wrap something to wrap against. */
  max-width: 100%;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: ${SPACE[8]};
  margin-left: auto;
`;

